<template>
  <v-form ref="form" >

    <BaseUtil
      ref="util"
      :pageLoading="pageLoading"
    />

    <v-row>
      <v-col cols="12">
        <v-switch 
          v-model="entry.isNewItemInStock" 
          @change="isNewItemInStockChanged"
          inset 
          :label="entry.isNewItemInStock ? 'Adiciona novo item ao estoque' : 'Ajuste de item que já está em estoque'"
          :readonly="!stockAdjustment.isStockEntryType"
          :disabled="readOnly"
        >
        </v-switch>
      </v-col>

      <v-col cols="12" v-if="!entry.isNewItemInStock">
        <v-autocomplete
          v-model="entry.itemId"
          v-bind="getObject('itemId')"
          label="Edição do Item"
          :items="extraData.accountContext.availableItemsForStock"
          item-value="id"
          item-text="name"
          @input="selectItem(true)"
          :hint="itemAdditionalInfo(entry.item)"
          persistent-hint
          autocomplete="off"
          :disabled="readOnly"
        >
          <template v-slot:item="data">
            <v-list-item-content>
              <v-list-item-title>{{data.item.name}}</v-list-item-title>
              <v-list-item-subtitle>{{itemAdditionalInfo(data.item)}}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
      
      <v-col cols="12" v-if="entry.isNewItemInStock">
        <v-autocomplete
          v-model="entry.itemId"
          v-bind="getObject('itemId')"
          label="Novo Item em Estoque"
          :items="itemsList"
          item-value="id"
          item-text="name"
          :search-input.sync="itemsSearch"
          no-filter
          @input="selectItem(true)"
          no-data-text="Nenhum Item encontrado, faça outra busca"
          :hint="itemAdditionalInfo(entry.item)"
          persistent-hint
          autocomplete="off"
          :disabled="readOnly"
        >
          <template v-slot:item="data">
            <v-list-item-content>
              <v-list-item-title>{{data.item.name}}</v-list-item-title>
              <v-list-item-subtitle>{{itemAdditionalInfo(data.item)}} </v-list-item-subtitle>
            </v-list-item-content>
          </template>

          <template v-slot:append-outer>
            <NewItemSelect
              ref="newItemSelect"
              newTitle="Adicionar Item"
              width="50%"
              openSelectButtonLabel="Adicionar Item"
              :inlineAddButton="true"
              @entry-created="itemCreated"
            />
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12">
        <v-autocomplete
          :disabled="!entry.itemId || readOnly"
          v-model="entry.packagingId"
          v-bind="getObject('packagingId')"
          label="Embalagem"
          :items="packagingList"
          item-value="id"
          item-text="name"
          @input="selectPackaging"
          :loading="packagingListLoading"
          autocomplete="off"
        >
          <!-- <template v-slot:append-outer>
            <PackagingNew
              :inlineAddButton="true"
              @entry-created="packagingCreated"
            />
          </template> -->
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6">
        <MaskedTextField
          v-if="renderComponent"
          :inputData.sync="packagingQuantity"
          mask-type="currency_10d"
          :label="'Quantidade de Embalagens ' + (stockAdjustment.isStockEntryType ? 'Adicionadas' : 'Removidas')"
          :disabled="!entry.packagingId || readOnly"
          @update:inputData="changePackagingQuantity" 
          :hint="entry.packagingId ? ('Quantidade de embalagens disponível em estoque: ' + numberToDecimal(calculatePackagingInStockQuantity) + ' ' + entry.packaging.text) : ''"
          :persistent-hint="entry.packagingId ? true : false"
          autocomplete="off"
          :suffix="entry.packaging ? entry.packaging.text : ''"
          :key="packagingQuantityKey + 'packagingQuantity'"
        />
        <!--   -->
      </v-col>

      <v-col cols="12" sm="6">
        <MaskedTextField
          v-if="renderComponent"
          v-bind="getObject('quantity')"
          :inputData.sync="entry.quantity"
          mask-type="currency_10d"
          :label="'Quantidade Unitária ' + (stockAdjustment.isStockEntryType ? 'Adicionada' : 'Removida')"
          :disabled="!entry.packagingId || readOnly"
          @update:inputData="changeQuantity"
          :hint="entry.packagingId ? ('Quantidade disponível em estoque: ' + numberToDecimal(availableQuantity) + entry.item.unit.name) : ''"
          :persistent-hint="entry.packagingId ? true : false"
          autocomplete="off"
          :suffix="entry.item ? entry.item.unit.name : ''"
          :key="quantityKey + 'quantity'"
        />
        <!-- -->
      </v-col>

      <!-- preço unitario -->
      <v-col cols="12" sm="6" md="4">
        <MaskedTextField
          v-if="renderComponent"
          v-bind="getObject('price')"
          :inputData.sync="entry.price"
          mask-type="currency_10d"
          label="Preço Unitário"
          prefix="R$"
          :suffix="entry.item ? entry.item.unit.name : ''" 
          :hint="entry.packagingId ? (itemLastPriceInStock > 0 ? ('Último Preço Unitário Cadastrado neste Estoque') : ('Último Preço Unitário Cadastrado no Sistema')) : ''"
          :persistent-hint="entry.packagingId ? true : false"
          :disabled="!entry.packagingId || !stockAdjustment.isStockEntryType || readOnly"
          :key="unitPriceKey + 'unitPrice'"
          @update:inputData="changeUnitPrice"
          autocomplete="off"
        />
      </v-col>

      <!-- Preço da embalagem -->
      <v-col cols="12" sm="6" md="4">
        <MaskedTextField
          v-if="renderComponent"
          :inputData.sync="entry.packagingPrice"
          mask-type="currency_10d"
          label="Preço Embalagem"
          prefix="R$"
          :suffix="entry.packaging ? entry.packaging.text : ''"
          :disabled="!entry.packagingId || !stockAdjustment.isStockEntryType || readOnly"
          :key="packagingPriceKey + 'packagingPrice'"
          @update:inputData="changePackagingPrice"
          autocomplete="off"
        />
      </v-col>

      <!-- preço total -->
      <v-col cols="12" sm="6" md="4">
        <MaskedTextField
          v-if="renderComponent"
          v-bind="getObject('totalPrice')"
          :inputData.sync="entry.totalPrice"
          mask-type="currency"
          :label="'Preço Total ' + (stockAdjustment.isStockEntryType ? 'Adicionado' : 'Removido')"
          prefix="R$"
          :disabled="!entry.packagingId || !stockAdjustment.isStockEntryType || readOnly"
          :key="totalPriceKey + 'totalPrice'"
          @update:inputData="changeTotalPrice"
          autocomplete="off"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import MaskedTextField from "@shared_components/masked_text_field.vue"
import gql from 'graphql-tag'
import PackagingNew from "@models/packagings/new.vue"
import BaseUtil from "@shared_components/base_util.vue"
import NewItemSelect from "@models/purchase_items/new_item_select.vue"

export default {
  data() {
    return {
      entryAttributes: {},
      itemsList: [],
      itemsListLoading: false,
      packagingListLoading: false,
      packagingList: [],
      renderComponent: true,
      itemsSearch: "",
      unitPriceKey: 0,
      totalPriceKey: 0,
      packagingQuantity: 0,
      packagingQuantityKey: 0,
      quantityKey: 0,
      packagingPriceKey: 0,
      availableQuantityLoading: false,
      availableQuantity: 0,
      itemLastPrice: 0,
      itemLastPriceInStock: 0,
      pageLoading: false,
    };
  },
  props: {
    entry: Object,
    extraData: Object,
    stockAdjustment: Object,
    isEdit: { // utilizado para definir se o form é de edição
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    modelNameSingular: String,
  },
  components: {
    MaskedTextField: MaskedTextField,
    PackagingNew: PackagingNew,
    BaseUtil: BaseUtil,
    NewItemSelect: NewItemSelect
  },
  created() {
    this.entryAttributes = this.setupEntryAttributes(this.entry);
    // console.log("item", this.entry.item)
    if (this.entry.item) {
      this.itemsList = [this.entry.item];
      this.selectItem(false);
      this.changeQuantity();
    }
  },
  mounted() {
  },
  computed: {
    calculatePackagingInStockQuantity: function() {
      return _.round(this.availableQuantity / this.entry.packaging.size, 10);
    },
  },
  watch: {
    itemsSearch: function(newSearch, oldSearch) {
      // console.log("newSearch", newSearch, "oldSearch", oldSearch)
      if (this.itemsSearch && this.itemsSearch.length > 0) {
        if (!this.entry.item || this.entry.item.name != this.itemsSearch) {
          this.getItems();
        }
      }
    }
  },
  methods: {
    itemAdditionalInfo: function(item) {
      let info = ""
      if (item) {
        info = 'Unidade: ' + item.unit.name + (item.brand ? ', Marca: ' + item.brand.name : '') + (item.typeText ? ', Tipo: ' + item.typeText : '')
      }
      return info;
    },
    changeQuantity: function() {
      this.packagingQuantity = _.round(this.entry.quantity / this.entry.packaging.size, 10);
      this.packagingQuantityKey++;
      this.updateQuantity();
    },
    updateQuantity: function() {
      if (this.entry.price > 0) {
        this.calculatePackagingPrice();
        this.calculateTotalPrice();
      } else if (this.entry.totalPrice > 0) {
        this.calculateUnitPrice();
        this.calculatePackagingPrice();
      } else {
        this.entry.totalPrice = 0;
        this.totalPriceKey++;
        this.entry.price = 0;
        this.unitPriceKey++;
        this.entry.packagingPrice = 0;
        this.packagingPriceKey++;
      }
    },
    changePackagingQuantity: function() {
      this.entry.quantity = _.round(this.packagingQuantity * this.entry.packaging.size, 10);
      this.quantityKey++;
      this.updateQuantity();
    },
    changeUnitPrice: function() {
      if (this.entry.quantity > 0) {
        this.calculatePackagingPrice();
        this.calculateTotalPrice();
      }
    },
    changePackagingPrice: function() {
      if (this.entry.quantity > 0) {
        this.calculateUnitPrice(false);
        this.calculateTotalPrice();
      }
    },
    changeTotalPrice: function() {
      if (this.entry.quantity > 0) {
        this.calculateUnitPrice();
        this.calculatePackagingPrice();
      }
    },
    calculateTotalPrice: function() {
      this.entry.totalPrice = _.round((this.entry.price ? this.entry.price : 0) * (this.entry.quantity ? this.entry.quantity : 0), 2);
      this.totalPriceKey++;
    },
    calculateUnitPrice: function(byTotalPrice=true) {
      if (byTotalPrice) {
        this.entry.price = _.round((this.entry.totalPrice ? this.entry.totalPrice : 0) / (this.entry.quantity ? this.entry.quantity : 0), 10);
      } else { //calcula pelo preco unitario com base no preco da embalagem
        this.entry.price = _.round((this.entry.packagingPrice ? this.entry.packagingPrice : 0) / (this.entry.packaging.size ? this.entry.packaging.size : 0), 10);
      }
      this.unitPriceKey++;
    },
    calculatePackagingPrice: function() {
      this.entry.packagingPrice = _.round((this.entry.price ? this.entry.price : 0) * (this.entry.packaging.size ? this.entry.packaging.size : 0), 10);
      this.packagingPriceKey++;
    },
    getObject: function(element, component) {
      return this.entryAttributes[element];
    },
    isFormValid: function() {
      if (this.$refs.form.validate()) {
        return true;
      } else {
        setTimeout(function(){
          document.querySelector(".v-messages.error--text:first-of-type").scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }
      return false;
    },
    resetFormInputs: function() {
      this.$refs.form.resetValidation();
    },
    isNewItemInStockChanged: function() {
      // if (this.entry.isNewItemInStock) {
        this.entry.itemId = null;
        this.itemsList = [];
        this.itemsSearch = "";
        this.entry.packagingId = null;
        this.entry.packaging = null;
        this.packagingList = [];
        this.entry.quantity = null;
        this.packagingQuantity = 0;
        this.entry.price = null;
        this.entry.packagingPrice = null;
        this.entry.totalPrice = null;
        this.unitPriceKey++;
        this.totalPriceKey++;
        this.packagingQuantityKey++;
        this.quantityKey++;
        this.packagingPriceKey++;
      // } 
    },
    getItems: function() {
      this.itemsListLoading = true;
      this.$apollo
        .query({
          query: ITEMS_SEARCH_QUERY,
          variables: {
            query: this.itemsSearch
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.itemsList = response.data.accountGlobalSharedContext.allAvailableItemsForPurchase;
          // if (this.packageList.length == 1 && !this.entry.packageId) {
          //   this.entry.packageId = this.packageList[0].id;
          //   this.selectPackage();
          // }
        })
        .catch(response => console.log(response))
        .finally(() => (this.itemsListLoading = false));
    },
    selectItem: function(clearPackaging, newPackaging=null) {
      

      // console.log("item, itemID", this.entry.item, this.entry.itemId)

      // this.clearFields();
      if (clearPackaging) {
        this.entry.packagingId = null;
        this.entry.packaging = null;
        this.packagingList = [];
      }
      this.packagingListLoading = true;
      
      if (this.entry.isNewItemInStock) {
        this.entry.item = this.itemsList.find(obj => obj.id == this.entry.itemId);

        this.$apollo
          .query({
            query: PACKAGINGS_FOR_ITEM_QUERY,
            fetchPolicy: "no-cache",
            variables: {
              itemId: this.entry.itemId
            }
          })
          .then(response => {
            this.packagingList = response.data.accountGlobalSharedContext.allPackagingsOfItem;

            // console.log("this.packagingList",this.packagingList);

            if (newPackaging) {
              let result_find = this.packagingList.find(obj => obj.id == newPackaging.id);
              if (result_find) {
                this.entry.packagingId = newPackaging.id;
                this.entry.packaging = newPackaging;
                // this.selectPackaging();
              } else {
                this.$refs.util.showErrorMessage("A embalagem criada não é compatível com o item selecionado, por isso não irá aparecer na lista de embalagens!");
              }
            } else if (this.packagingList.length == 1 && !this.entry.packagingId) {
              this.entry.packagingId = this.packagingList[0].id;
              this.entry.packaging = this.packagingList.find(obj => obj.id == this.entry.packagingId);
              // this.selectPackage();
            }
          })
          .catch(response => console.log(response))
          .finally(() => (this.packagingListLoading = false));
      } else {
        this.entry.item = this.extraData.accountContext.availableItemsForStock.find(obj => obj.id == this.entry.itemId);

        this.$apollo
          .query({
            query: AVAILABLE_PACKAGING_FOR_ITEM_QUERY,
            fetchPolicy: "no-cache",
            variables: {
              itemId: this.entry.itemId,
              stockId: this.stockAdjustment.stockId
            }
          })
          .then(response => {
            // console.log("response dos packaging", response);
            this.packagingList = response.data.accountContext.availableItemPackagingsForStock;

            // if (this.entry.item && this.isEdit) {
            //   let packagingFromList = this.packagingList.find(obj => obj.id == this.entry.packagingId)
            //   if (!packagingFromList) {
            //     this.packagingList.unshift(this.entry.packaging)
            //   }
            // }

            if (this.packagingList.length == 1 && !this.entry.packagingId) {
              this.entry.packagingId = this.packagingList[0].id;
            }
            this.selectPackaging();
          })
          .catch(response => console.log(response))
          .finally(() => (this.packagingListLoading = false));
      }
    },
    selectPackaging: function() {
      this.entry.packaging = this.packagingList.find(obj => obj.id == this.entry.packagingId);

      if (!this.entry.isNewItemInStock) {
        this.availableQuantityLoading = true;
        this.$apollo
          .query({
            query: AVAILABLE_QUANTITY_AND_PRICE_FOR_ITEM_QUERY,
            fetchPolicy: "no-cache",
            variables: {
              itemId: this.entry.itemId,
              packagingId: this.entry.packagingId,
              stockId: this.stockAdjustment.stockId
            }
          })
          .then(response => {
            // console.log("response dos stocks", response)
            this.availableQuantity = response.data.accountContext.availableQuantityForItem;
            this.itemLastPrice = response.data.accountContext.lastPriceForItem;
            this.itemLastPriceInStock = response.data.accountContext.lastPriceForItemInStock;

            // console.log(this.availableQuantity, this.itemLastPrice, this.itemLastPriceInStock)

            if (this.itemLastPriceInStock > 0 ) {
              this.entry.price = this.itemLastPriceInStock;
              this.unitPriceKey++;
              this.changeUnitPrice();
            } else if (this.itemLastPrice > 0 ) {
              this.entry.price = this.itemLastPrice;
              this.unitPriceKey++;
              this.changeUnitPrice();
            }

            if (!this.stockAdjustment.isStockEntryType) {
              this.entryAttributes.quantity.rules = [];
              this.entryAttributes.quantity.rules.push(this.requiredRule);
              this.entryAttributes.quantity.rules.push(this.minRequiredRule);
              let availableQuantity = _.round(this.availableQuantity, 10);
              // usado para contabilizar a quantidade já salva no edit
              if (this.isEdit) {
                availableQuantity = _.round(availableQuantity + this.originalEntry.quantity, 10);
              }
              this.entryAttributes.quantity.rules.push(this.maxValueRule(availableQuantity));
            }
          })
          .catch(response => console.log(response))
          .finally(() => (this.availableQuantityLoading = false));
      } else {
        this.changePackagingQuantity();
      }

      // return;
      // this.entryAttributes.quantity.rules = [];
      // this.entryAttributes.quantity.rules.push(this.requiredRule);
      // this.entryAttributes.quantity.rules.push(this.minRequiredRule);
      // // this.entryAttributes.quantity.rules.push(this.maxValueRule(this.entry.storehouse.availableQuantity));

      // this.entryAttributes.price.rules = [];
      // this.entryAttributes.price.rules.push(this.requiredRule);
      // this.entryAttributes.price.rules.push(this.minRequiredRule);
      // this.clearFields();
    },
    clearFields: function() {
      this.forceRerender(this);
    },
    packagingCreated: function(packaging) {
      this.selectItem(true, packaging);
    },
    itemCreated: function(item) {
      // console.log("item created", item)
      this.itemsList.unshift(item);
      this.entry.itemId = item.id;
      this.entry.item = item;
      this.selectItem(true);
    },
  }
};

const ITEMS_SEARCH_QUERY = gql `
  query ($query: String!) {
    accountGlobalSharedContext {
      allAvailableItemsForPurchase(query: $query) {
        id
        name
        unit {
          name
        }
        brand {
          name
        }
        typeText
      }
    }
  }
`;

const PACKAGINGS_FOR_ITEM_QUERY = gql `
  query ($itemId: ID!) {
    accountGlobalSharedContext {
      allPackagingsOfItem(itemId: $itemId) {
        id
        name
        size
        text
      }
    }
  }
`;

const AVAILABLE_PACKAGING_FOR_ITEM_QUERY = gql `
  query ($itemId: ID!, $stockId: ID!) {
    accountContext {
      availableItemPackagingsForStock(itemId: $itemId, stockId: $stockId){
        id
        name
        text
        size
      }
    }
  }
`;

const AVAILABLE_QUANTITY_AND_PRICE_FOR_ITEM_QUERY = gql `
  query ($itemId: ID!, $packagingId: ID!, $stockId: ID!) {
    accountContext {
      availableQuantityForItem(itemId: $itemId, packagingId: $packagingId, stockId: $stockId)
      lastPriceForItem(itemId: $itemId, packagingId: $packagingId)
      lastPriceForItemInStock(itemId: $itemId, packagingId: $packagingId, stockId: $stockId)
    }
  }
`;

</script>

<style scoped></style>
